import React, {useEffect, useState} from 'react'
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import TableContainer from '@mui/material/TableContainer';
import get from 'lodash.get';
import {useQuery} from 'react-query'
import Tooltip from '@mui/material/Tooltip';
import {IconButton, Button} from "@mui/material";
import {Delete} from "@mui/icons-material";
// ✅ CORRECT
import Checkbox from "@mui/material/Checkbox";
import {isNotInsurerReadOnlyUser, isVitrayaInternalUser} from "../../constants/UserAuth";
import usePreventScroll from './PreventScroll';
import {updateIrdaPayable} from "../../utils/API";
import {showToast} from "../../utils/tostr";
import {PackageLineBadge} from "../viewClaim/PackageConstruct";

const MASTER_CATEGORIES = [
    "Ambulance Charges",
    "Anaesthesia",
    "Blood & Blood Products",
    "Cathlab Charges",
    "Dental Charges, Cosmetic Charges",
    "Doctor Fees",
    "Food & Beverages",
    "ICU Charges",
    "Implants & Prosthetic Charges",
    "Investigation Charges",
    "Medical Equipment",
    "Medicines & Consumables",
    "Minimally Invasive Procedure Charges",
    "OT Charges",
    "Physiotherapy",
    "Procedure Charges",
    "Room & Nursing Charges",
    "Service Tax & Other Taxes",
    "Surgical Package",
    "Taxes & Miscellaneous Charges",
]


const CustomTable2CellInternal = ({cell, flexRender}) => {
    return (
        <TableCell>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
    )
}

const CustomTable2Cell = React.memo(CustomTable2CellInternal);

const CustomTable2RowInternal = ({color, visibleCells, flexRender}) => {
    return (
        <TableRow style={{background: color || '#ffffff'}}>
            {visibleCells.map((cell, index) => (
                <CustomTable2Cell key={index} cell={cell} flexRender={flexRender} />
            ))}
        </TableRow>
    )
}

const CustomTable2Row = React.memo(CustomTable2RowInternal);


const EditableMasterCategory = (props) => {
    const {
        cell,
        row: {index},
        column: {id},
        table, // This is a custom function that we supplied to our table instance
    } = props
    // We need to keep and update the state of the cell normally
    const [value, setValue] = useState(cell.getValue())
    const { storeScrollPosition, restoreScrollPosition } = usePreventScroll();

    const onChange = (e, newValue) => {
        setValue(newValue)
        storeScrollPosition();
        table.options?.meta?.updateData(index, id, newValue);
        restoreScrollPosition();
    }

    // We'll only update the external data when the input is blurred
    // const onBlur = () => {
    //     table.options?.meta?.updateData(index, id, value)
    // }

    // If the initialValue is changed external, sync it up with our state
    useEffect(() => {
        setValue(cell.getValue())
    }, [cell.getValue()])

    return <Tooltip title={value}>
        <Autocomplete
            id="free-solo-demo"
            blurOnSelect
            freeSolo
            value={value}
            size='small'
            onChange={onChange}
            disableClearable
            disabled={!isNotInsurerReadOnlyUser()}
            // onBlur={onBlur}
            options={MASTER_CATEGORIES}
            renderInput={(params) => <TextField
                {...params}
                sx={{width: '10rem', background: '#ffffff'}}
                // InputProps={{sx: {fontSize: '0.8rem'}}}
                size='small'/>}
        />
    </Tooltip>
}

const PCP_NON_PAYABLE_TEXT = "Non-Payable by Procedure Construct";

const isPcpNonPayable = (rowData) =>
    get(rowData, ['procedure_construct_payable_text']) === PCP_NON_PAYABLE_TEXT;

const isIpNonPayable = (rowData) => get(rowData, ['irdai_payable']) === false;

const EditableIRDAIPayableToggle = (props) => {
    const {
        cell,
        row: {index},
        column: {id},
        table, // This is a custom function that we supplied to our table instance
    } = props
    // We need to keep and update the state of the cell normally
    const [value, setValue] = useState(cell.getValue())
    const {storeScrollPosition, restoreScrollPosition} = usePreventScroll();
    const onClick = () => {
        const newValue = !value;
        setValue(newValue);

        storeScrollPosition();
        table.options?.meta?.updateData(index, id, newValue);
        restoreScrollPosition();

        // IP and PCP both have to be payable for the row to carry an ABHI amount - turning IP back
        // to payable leaves it at 0 while PCP still says non-payable.
        const isPayable = newValue && !isPcpNonPayable(cell.row.original);
        const totalBillAmount = cell.row.getValue('total_bill_amount');
        table.options?.meta?.updateData(index, 'insurerAmount', isPayable ? totalBillAmount : 0);

        // ✅ RESET CHECKBOX HERE
        table.options?.meta?.clearRowSelection(cell.row.original.row_id);


        // ✅ enable checkbox
        table.options.meta.setRowEnabled(cell.row.original.row_id, true);
    }

    // We'll only update the external data when the input is blurred
    // const onBlur = () => {
    //     table.options?.meta?.updateData(index, id, value)
    // }

    // If the initialValue is changed external, sync it up with our state
    useEffect(() => {
        setValue(cell.getValue())
    }, [cell.getValue()])

    return (
        <Chip
            label={value ? "P" : "NP"}
            color={value ? "success" : "error"}
            onClick={onClick}
            disabled={!isNotInsurerReadOnlyUser()}
            variant="outlined"
            style={{padding: 2}}
        />
    );
}



const shortFormProcedureConstructTxt = {
    "Payable by Procedure Construct": "P",
    "Payable": "P",
    "Partially Payable by Procedure Construct": "PP",
    "Non-Payable by Procedure Construct": "NP",
};

const PCP_LOCK_HINT =
    "PCP can't be changed here - use the Procedure Construct panel above to make it payable or non-payable.";


const EditableProcedureConstructPayableToggle = (props) => {
    const {
        cell,
        row: {index},
        column: {id},
        table,
    } = props;

    const [value, setValue] = useState(cell.getValue());
    const {storeScrollPosition, restoreScrollPosition} = usePreventScroll();

    const updateValues = (newValue) => {
        const totalBillAmount = cell.row.getValue('total_bill_amount');
        const procedureConstructPayableText = newValue
            ? "Payable by Procedure Construct"
            : "Non-Payable by Procedure Construct";
        // Same rule from the PCP side: a row IP has marked non-payable stays at 0 either way.
        const insurerAmount = newValue && !isIpNonPayable(cell.row.original) ? totalBillAmount : 0;
        // console.log(cell.row.getValue('procedure_construct_payable'), cell.row.getValue('procedure_construct_payable_text'));

        storeScrollPosition();
        table.options?.meta?.updateData(index, 'procedure_construct_payable', newValue);
        table.options?.meta?.updateData(index, 'insurerAmount', insurerAmount);
        table.options?.meta?.updateData(index, id, procedureConstructPayableText);
        restoreScrollPosition();
    };

    const onClick = () => {
        let isPayable = (shortFormProcedureConstructTxt[value] !== 'NP');
        const newValue = !isPayable;
        setValue(newValue);
        updateValues(newValue);
    };



    const colorMap = {
        "Payable by Procedure Construct": "success",
        "Payable": "success",
        "Partially Payable by Procedure Construct": "primary",
        "Non-Payable by Procedure Construct": "error",
    };

    useEffect(() => {
        setValue(cell.getValue());
    }, [cell.getValue()]);

    // A disabled chip takes no mouse events of its own, so the reason it cannot be clicked
    // hangs off a wrapper around it - otherwise the tooltip never opens.
    return (
        <Tooltip title={PCP_LOCK_HINT}>
            <span style={{display: 'inline-block', cursor: 'not-allowed'}}>
                <Chip
                    label={shortFormProcedureConstructTxt[value] || value}
                    color={colorMap[value] || "error"}
                    variant="outlined"
                    style={{padding: 2}}
                    onClick={onClick}
                    // Disabled by default: the Procedure Construct verdict is set from the PC panel, not
                    // toggled per row here. Put back `!isNotInsurerReadOnlyUser()` to make it clickable again.
                    disabled
                    // Shut, but not faded - the chip keeps its own P / PP / NP colour so it still reads.
                    sx={{'&.Mui-disabled': {opacity: 1}}}
                />
            </span>
        </Tooltip>
    );
};

const EditableRemark = (props) => {
    const {
        cell,
        row: {index},
        column: {id},
        table, // This is a custom function that we supplied to our table instance
    } = props
    // We need to keep and update the state of the cell normally
    const [value, setValue] = useState(cell.getValue())
    const { storeScrollPosition, restoreScrollPosition } = usePreventScroll();

    const onChange = (e) => {
        setValue(e.target.value)
    }

    // We'll only update the external data when the input is blurred
    const onBlur = () => {
        storeScrollPosition(); // save scroll before the updation of the data
        table.options?.meta?.updateData(index, id, value);
        restoreScrollPosition(); // restoring the scroll 
    }

    // If the initialValue is changed external, sync it up with our state
    useEffect(() => {
        setValue(cell.getValue())
    }, [cell.getValue()])

    return <Tooltip title={value ?? ''}><TextField
        value={value ?? ''}
        onChange={onChange}
        onBlur={onBlur}
        variant='outlined'
        size='small'
        style={{background: '#ffffff'}}
        disabled={!isNotInsurerReadOnlyUser()}
        InputProps={{
            style: {fontSize: '0.8rem', width: "100%"}
        }}/></Tooltip>
}

/**
 * A line item the Procedure Construct has cut back, or one the IRDAI list keeps off the claim,
 * has no ABHI amount left for a reviewer to type. Its figure follows the payability decision -
 * the PC panel and the IP / PCP chips write it - so the box is held shut until that decision is
 * taken, rather than letting a number be entered that the next toggle would overwrite.
 */
const PC_LOCKED_VERDICTS = [
    "Partially Payable by Procedure Construct",
    "Non-Payable by Procedure Construct",
];

// Said in the cell's tooltip and again in the callout the panel raises when the reviewer is
// sent up to it, so the two read as one instruction rather than two different ones.
export const ABHI_AMOUNT_LOCK_HINT =
    'Please make the line item payable first, then change the amount.';

const isAbhiAmountLocked = (rowData) => {
    const verdict = get(rowData, ['procedure_construct_payable_text']);
    return PC_LOCKED_VERDICTS.includes(verdict);
};

const EditableAmount = (props) => {
    const {
        cell,
        row,
        row: {index},
        column: {id},
        table, // This is a custom function that we supplied to our table instance
    } = props
    // We need to keep and update the state of the cell normally
    const [value, setValue] = useState(cell.getValue())
    const [error, setError] = useState(false)
    const { storeScrollPosition, restoreScrollPosition } = usePreventScroll();

    const totalBillAmount = Number(row.getValue('total_bill_amount'))
    const rowId = row.original?.row_id ?? index
    // Read off the row rather than off a cached copy: the IP / PCP chips rewrite the verdict in
    // place, so the box has to shut and open again on the same render they do.
    const locked = isAbhiAmountLocked(row.original)

    const onChange = e => {
        const newValue = e.target.value
        setValue(newValue)

        const isInvalid = newValue !== '' && Number(newValue) > totalBillAmount
        setError(isInvalid)
        table.options?.meta?.setAmountValidationError(rowId, isInvalid)
        if (isInvalid) {
            showToast(
                `ABHI Amount cannot be more than Total Bill Amount (₹${totalBillAmount})`,
                'error',
                `abhi_amount_exceeds_total_bill_${rowId}`
            )
        }
    }

    // We'll only update the external data when the input is blurred
    const onBlur = () => {
        if (Number(value) > totalBillAmount) {
            return
        }
        storeScrollPosition();
        table.options?.meta?.updateData(index, id, value)
        restoreScrollPosition();
    }

    // If the initialValue is changed external, sync it up with our state
    useEffect(() => {
        setValue(cell.getValue())
        setError(false)
        table.options?.meta?.setAmountValidationError(rowId, false)
    }, [cell.getValue()])

    // A row that locks while it was carrying an over-bill figure would hold Save Tariff shut with
    // no box left to correct it in, so the lock drops the tracked error with the input.
    useEffect(() => {
        if (locked) {
            setError(false)
            table.options?.meta?.setAmountValidationError(rowId, false)
        }
    }, [locked, rowId])

    // Clear the tracked error if this row is unmounted (e.g. pagination) while still invalid
    useEffect(() => () => table.options?.meta?.setAmountValidationError(rowId, false), [rowId])

    const isEqualToTotalBillAmount = value !== '' && Number(value) === totalBillAmount

    const amountField = <TextField
        type='number'
        value={value ?? ''}
        onChange={onChange}
        onBlur={onBlur}
        variant='outlined'
        size='small'
        style={{background: '#ffffff'}}
        disabled={locked || !isNotInsurerReadOnlyUser()}
        error={error}
        helperText={error ? 'Cannot exceed Total Bill Amount' : ''}
        // A disabled input takes no mouse events of its own in most browsers, and swallows the
        // ones meant for the wrapper underneath it. Handing them straight past it is what lets
        // the hint appear and the click land while the box stays shut.
        sx={locked ? {pointerEvents: 'none'} : undefined}
        InputProps={{
            style: {fontSize: '0.8rem'}
        }}/>

    if (!locked) {
        return amountField
    }

    // A disabled input fires nothing of its own, so the hint and the click both hang off a
    // wrapper around it - the click carries the reviewer up to the panel that can unlock the row.
    return <Tooltip title={ABHI_AMOUNT_LOCK_HINT}>
        <span
            onClick={() => table.options?.meta?.onAmountLockedClick?.(row.original)}
            style={{display: 'inline-block', width: '100%', cursor: 'not-allowed'}}>
            {amountField}
        </span>
    </Tooltip>
}

const RowSelectCheckbox = ({ row, table }) => {
    const rowData = row.original;
    const rowId = rowData.row_id;
    const selectedRows = table.options.meta.selectedRows || {};
    const enabledRows = table.options.meta.enabledRows || {};   // ✅ FIX

    const isChecked = Boolean(selectedRows[rowId]);
    const isEnabled = Boolean(enabledRows[rowId]);



    const handleChange = (e) => {
        table.options.meta.toggleRowSelection(
            rowId,
            rowData,
            e.target.checked
        );


    };

    return (
        <Checkbox
            checked={isChecked}
            onChange={handleChange}
            disabled={!isEnabled}   // ✅ NOW WORKS
            size="small"
        />
    );
};




const TextSwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 32,
    padding: 0,

    '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
            transform: 'translateX(30px)',
            color: '#fff',

            '& + .MuiSwitch-track': {
                backgroundColor: '#4caf50',
                opacity: 1,
            },

            '& .MuiSwitch-thumb:before': {
                content: '"P"',
            },
        },
    },

    '& .MuiSwitch-thumb': {
        backgroundColor: '#fff',
        width: 28,
        height: 28,
        position: 'relative',

        '&:before': {
            content: '"NP"',
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            color: '#000',
        },
    },

    '& .MuiSwitch-track': {
        borderRadius: 16,
        backgroundColor: '#f44336',
        opacity: 1,
    },
}));

// const RowSelectToggle = ({ row, table }) => {
//     const rowData = row.original;
//     const rowId = rowData.row_id;
//
//     const selectedRows = table.options.meta.selectedRows || {};
//     const enabledRows = table.options.meta.enabledRows || {};
//
//     const isChecked = Boolean(selectedRows[rowId]);
//     const isEnabled = Boolean(enabledRows[rowId]);
//
//     const handleChange = async (e) => {
//         const checked = e.target.checked;
//
//         table.options.meta.toggleRowSelection(rowId, rowData, checked);
//
//     };
//
//     return (
//         <TextSwitch
//             checked={isChecked}
//             onChange={handleChange}
//             disabled={!isEnabled}
//         />
//     );
// };

const CustomTable2Internal = ({
                                  originalData,
                                  setOriginalData,
                                  setUpdatedRows,
                                  addedRowIds,
                                  deleteRowIdsByRowId,
                                  handleClickOpen,
                                  setPreFillData,
                                  isTariff = false,
                                  claimId,
                                  onValidationChange,
                                  onAmountLockedClick
                              }) => {

    const columnHelper = createColumnHelper();
    const {storeScrollPosition, restoreScrollPosition} = usePreventScroll();
    const [selectedRows, setSelectedRows] = useState({}); // ✅ ADD HERE
    const [invalidAmountRowIds, setInvalidAmountRowIds] = useState(new Set());

    useEffect(() => {
        onValidationChange?.(invalidAmountRowIds.size > 0);
    }, [invalidAmountRowIds, onValidationChange]);
    const [enabledRows, setEnabledRows] = useState({});
    const email = localStorage.getItem("email");

    // ✅ ADD THIS HERE (IMPORTANT)
    const initializedRef = React.useRef(false);
    useEffect(() => {
        if (initializedRef.current) return;

        const initialSelected = {};

        originalData.forEach((row) => {
            if (row.irdai_marked === true) {
                initialSelected[row.row_id] = row;
            }
        });

        setSelectedRows(initialSelected);
        initializedRef.current = true;

    }, [originalData]);
    const columns = [
        columnHelper.accessor('master_category', {
            header: 'Master Category',
            cell: EditableMasterCategory
        }),
        columnHelper.accessor('bill_line_item', {
            header: 'Bill Line Item',
            cell: (info) =>
                <p
                    style={{
                        overflow: "auto",
                    }}
                >
                    {info.getValue()}
                    <PackageLineBadge row={info.row.original}/>
                </p>
        }),
        // columnHelper.display({
        //     id: 'select',
        //     header: 'Toggle',
        //     cell: (info) => (
        //         <RowSelectToggle row={info.row} table={info.table} />
        //     )
        // }),
        columnHelper.display({
            id: 'select',
            header:'Select',
            cell: (info) => (
                <RowSelectCheckbox row={info.row} table={info.table} />
            )
        }),

        columnHelper.accessor('irdai_payable', {
            header: 'IP',
            cell: EditableIRDAIPayableToggle
        }),
        columnHelper.accessor('procedure_construct_payable_text', {
            header: 'PCP',
            cell: EditableProcedureConstructPayableToggle
        }),
        ...(isVitrayaInternalUser() ? [
                columnHelper.accessor('tariff_application_reference_text', {
                    // show this column only if isVitrayaInternalUser()
                    header: 'Tariff Application Text',
                    cell: (info) =>
                        <p
                            style={{
                                overflow: "auto",
                            }}
                        >
                            {info.getValue()}
                        </p>
                })] : []
        ),
        columnHelper.accessor('unit', {
            header: 'Unit'
        }),
        columnHelper.accessor('unit_amount', {
            header: 'Unit Amount',
            cell: (info) =>
                <p> &#x20b9; {Math.trunc(info.getValue() * 100) / 100}</p>
        }),
        columnHelper.accessor('total_bill_amount', {
            header: 'Total Bill Amount',
            cell: (info) =>
                info.getValue() > info.row.getValue('admissible_amount') &&
                Math.trunc(info.row.getValue('savings')) > 0 ? (
                    <b>&#x20b9; {Math.trunc(info.getValue() * 100) / 100}</b>
                ) : (
                    <p>&#x20b9; {Math.trunc(info.getValue() * 100) / 100}</p>
                ),
        }),
        /*columnHelper.accessor('tariff_rate', {
            header: 'Tariff Rate',
            size: '200px',
            cell: (info) =>
                info.getValue() > info.row.getValue('admissible_amount') &&
                Math.trunc(info.row.getValue('savings')) > 0 ? (
                    <b>&#x20b9; {Math.trunc(info.getValue() * 100) / 100}</b>
                ) : (
                    <p>&#x20b9; {Math.trunc(info.getValue() * 100) / 100}</p>
                ),
        }),*/
        columnHelper.accessor('admissible_amount', {
            header: 'Tariff Amount',
            cell: (info) =>
                info.getValue() > info.row.getValue('admissible_amount') &&
                Math.trunc(info.row.getValue('savings')) > 0 ? (
                    <b>&#x20b9; {Math.trunc(info.getValue() * 100) / 100}</b>
                ) : (
                    <p>&#x20b9; {Math.trunc(info.getValue() * 100) / 100}</p>
                ),
        }),
        columnHelper.accessor('savings', {
            header: 'Savings',
            cell: (info) =>
                info.getValue() > info.row.getValue('admissible_amount') &&
                Math.trunc(info.row.getValue('savings')) > 0 ? (
                    <b>&#x20b9; {Math.trunc(info.getValue() * 100) / 100}</b>
                ) : (
                    <p>&#x20b9; {Math.trunc(info.getValue() * 100) / 100}</p>
                ),
        }),
        columnHelper.accessor('insurerAmount', {
            header: 'ABHI Amount',
            cell: EditableAmount
        }),
        columnHelper.accessor('remarks', {
            header: 'Remarks',
            cell: EditableRemark
        }),

        
        // ...(isVitrayaInternalUser() ? [
        //         columnHelper.accessor('delete', {
        //             header: 'Delete',
        //             cell: (info) => <IconButton
        //                 aria-label="toggle password visibility"
        //                 onClick={(e) => {
        //                     storeScrollPosition();
        //                     deleteRowIdsByRowId(get(info, ['row', 'original', 'row_id']));
        //                     restoreScrollPosition();
        //                 }}
        //                 edge="end"
        //             >
        //                 <Delete/>
        //                 <Button sx={{ml: '0.5rem'}} variant="outlined" onClick={handleClickOpen}>Add Row</Button>
        //             </IconButton>
        //         })] : []
        ...(isVitrayaInternalUser() ? [
            columnHelper.accessor('delete', {
                header: 'Actions',
                cell: (info) => get(info, ['row', 'original', 'is_package_line']) ? null : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        aria-label="delete row"
                        onClick={(e) => {
                            storeScrollPosition();
                            deleteRowIdsByRowId(get(info, ['row', 'original', 'row_id']));
                            restoreScrollPosition();
                        }}
                        edge="end"
                      >
                        <Delete />
                      </IconButton>

                    </div>
                )
            })
        ] : []
        ),
        ...(isVitrayaInternalUser() ? [
                columnHelper.accessor('pharma_return', {
                    header: 'Pharma Return',
                    cell: (info) => get(info, ['row', 'original', 'is_package_line']) ? null : (
                        <Button
                            sx={{ ml: '0.5rem' }}
                            variant="outlined"
                            onClick={() => {
                                const rowData = info.row.original;

                                setPreFillData({
                                    master_category: rowData.master_category.trim() || '',
                                    bill_line_item: rowData.bill_line_item.trim() || '',
                                    level2CategoryName: rowData.level2CategoryName || '',
                                    unit: rowData.unit || '',
                                    unit_amount: 0-(Number(rowData.unit_amount)).toString().trim() || 0,
                                    tariff_rate: 0-(Number(rowData.tariff_rate)).toString().trim() || 0,
                                    allowed_units_by_proc_construct: rowData.unit || '',
                                    irdai_payable: rowData.irdai_payable ?? false,
                                    remarks: "Pharmacy return item" || '',
                                    pharmacy_return_item_id: [rowData.row_id],
                                    pharmacy_return_found:true,
                                    identifier: "pharma_return",
                                });
                                handleClickOpen();
                            }}
                        >
                            Add Return
                        </Button>
                    )
                })
            ] : []
        ),
    ];

    // const [searchText, setSearchText] = useState('')
    // const [filteredData, setFilteredData] = useState(originalData || [])
    const [sorting, setSorting] = useState([])

    const [{pageIndex, pageSize}, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    })

    const fetchDataOptions = {
        pageIndex,
        pageSize,
    }

    const dataQuery = useQuery(
        ['data', fetchDataOptions, originalData],
        () => fetchData(fetchDataOptions, originalData),
        {keepPreviousData: false}
    )

    const defaultData = React.useMemo(() => [], [])

    const pagination = React.useMemo(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize]
    )

    function fetchData(options, data) {
        // Simulate some network latency
        const startIndex = options.pageIndex * options.pageSize;
        const endIndex = (options.pageIndex + 1) * options.pageSize;
        const slicedData = data.slice(startIndex, endIndex);
        const pageCount = Math.ceil(data.length / options.pageSize);
        return {rows: slicedData, pageCount: pageCount};
    }

    const table = useReactTable({
        data: dataQuery.data?.rows ?? defaultData,
        columns,
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        pageCount: dataQuery.data?.pageCount ?? -1,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        meta: {
            updateData: (rowIndex, columnId, value) => {
                setUpdatedRows(prev => prev.add(get(originalData, [pageIndex * pageSize + rowIndex, 'row_id'])))
                setOriginalData(old =>
                    old.map((row, index) => {
                        if (index === pageIndex * pageSize + rowIndex) {
                            return {
                                ...old[pageIndex * pageSize + rowIndex],
                                [columnId]: value,
                            }
                        }
                        return row
                    })
                )
            },
            // ✅ MULTI SELECT
            toggleRowSelection: (rowId, rowData, isChecked) => {
                setSelectedRows(prev => {
                    const updated = { ...prev };

                    if (isChecked) {
                        updated[rowId] = rowData;
                    } else {
                        delete updated[rowId];
                    }

                    return updated;
                });

                if (isChecked) {
                    try {
                        const bill_line_item = rowData.bill_line_item;
                        console.log("Check "+claimId+" "+bill_line_item);
                        //  fetch("/api/row-selected", {
                        //     method: "POST",
                        //     headers: { "Content-Type": "application/json" },
                        //     body: JSON.stringify({
                        //         rowId,
                        //         rowData
                        //     })
                        // });
                        updateIrdaPayable(claimId, bill_line_item,rowData.irdai_payable,email,rowData.row_id);
                    } catch (err) {
                        console.error("API error:", err);
                    }
                }
            },

            // ✅ ONLY CALL API WHEN CHECKED IS TRUE


            clearRowSelection: (rowId) => {
                setSelectedRows(prev => {
                    const updated = { ...prev };
                    delete updated[rowId];
                    return updated;
                });
            },
            selectedRows,
            // 🔥 NEW
            setRowEnabled: (rowId, isEnabled) => {
                setEnabledRows(prev => ({
                    ...prev,
                    [rowId]: isEnabled
                }));
            },

            enabledRows,

            // Handed down to the ABHI Amount cell so a locked row can send the reviewer to the
            // Procedure Construct panel, which is the only place the row can be made payable.
            onAmountLockedClick,

            setAmountValidationError: (rowId, hasError) => {
                setInvalidAmountRowIds(prev => {
                    if (hasError === prev.has(rowId)) {
                        return prev;
                    }
                    const updated = new Set(prev);
                    if (hasError) {
                        updated.add(rowId);
                    } else {
                        updated.delete(rowId);
                    }
                    return updated;
                });
            }

        },
    });

    // const isTextContained = (obj, text) => {
    //     const normalizedSearchText = text.toLowerCase();
    //     return Object.values(obj).some(value => {
    //         // Convert value to string if it's not already
    //         const stringValue = String(value).toLowerCase();
    //         return stringValue.includes(normalizedSearchText);
    //     });
    // };

    // useEffect(() => {
    //     setFilteredData(originalData.filter(item => isTextContained(item, searchText)))
    // }, [searchText])

    function tariffLineColorCoding(row, rowColor) {
        if (row?.original?.irdai_payable === true) {
            return rowColor !== "#fff8de" ? rowColor : "#ffffff";
        }
        if (row?.original?.irdai_payable === false) {
            rowColor = '#fff8de';
        }
        return rowColor;
    }

    return (
        <Paper sx={{width: '100%', overflow: 'hidden'}}>
            {/* <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'end',
                mr: '2rem',
                pt: '1rem',
                mb: '1rem',
            }}>
                <TextField
                    id='search-table'
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={(e) => setSearchText('')}
                                    onMouseDown={(e) => setSearchText('')}
                                    edge="end"
                                >
                                    <Clear />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    // value={searchText}
                    // onChange={earchText}
                    // onChange={(e) => setSearchText(e.target.value)}
                    variant="standard"
                />
            </Box> */}
            <TableContainer component={Paper}
                            sx={{display: "flex", flexDirection: "column", height: isTariff ? 'auto' : '780px'}}>

                <Table className={"tariff-table"}>
                    <TableHead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <TableCell
                                        onClick={header.column.getToggleSortingHandler()}
                                        title={
                                            header.column.getCanSort()
                                                ? header.column.getNextSortingOrder() === 'asc'
                                                    ? 'Sort ascending'
                                                    : header.column.getNextSortingOrder() === 'desc'
                                                        ? 'Sort descending'
                                                        : 'Clear sort'
                                                : undefined
                                        }
                                        key={header.id}>
                                        <TableSortLabel
                                            sx={{fontWeight: 'bold'}}
                                            // active={sorting.map(item => item.id).includes(header.column.columnDef.header)}
                                            direction={header.column.getNextSortingOrder() === 'asc' ? 'desc' : 'asc'}
                                        >
                                            {
                                                header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )
                                            }
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {table.getRowModel().rows.map(row => {
                            const rowColor = tariffLineColorCoding(row, row?.original?.color);
                            return (
                                <CustomTable2Row key={row.id} color={rowColor}
                                                 visibleCells={row.getVisibleCells()} flexRender={flexRender}/>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                showFirstButton
                showLastButton
                rowsPerPageOptions={[5, 10, 25, 50, 75, 100]}
                component="div"
                count={originalData.length}
                rowsPerPage={pageSize}
                page={pageIndex}
                slotProps={{
                    select: {
                        inputProps: {'aria-label': 'rows per page'},
                        native: true,
                    },
                }}
                onPageChange={(_, page) => {
                    table.setPageIndex(page)
                }}
                onRowsPerPageChange={e => {
                    const size = e.target.value ? Number(e.target.value) : 10
                    table.setPageSize(size)
                }}
                // ActionsComponent={TablePaginationActions}
            />
        </Paper>

    );
}

const CustomTable2 = React.memo(CustomTable2Internal);

export default CustomTable2
